/**
 * 아이콘 — [Heroicons](https://heroicons.com) 24px outline.
 *
 * `@heroicons/react` 패키지 대신 **path를 그대로 옮겨 둔다.** 쓰는 아이콘이
 * 몇 개뿐이고, 아이콘은 한 번 정하면 바뀌지 않는 정적 자산이라 의존성을 하나
 * 더 들일 만큼의 값을 하지 않는다. 이 저장소는 `npm i` 한 번에 rollup·esbuild
 * 네이티브 모듈이 사라지는 일이 반복돼서(README 참고) 설치를 늘릴 이유가 없다.
 *
 * 아이콘이 늘어 관리가 번거로워지면 `npm i @heroicons/react` 한 줄로 갈아타면
 * 된다 — 아래 이름이 패키지의 이름과 같다.
 *
 * path는 `optimized/24/outline/{name}.svg`에서 **손대지 않고** 가져왔다.
 * 고치지 마라. 새 아이콘이 필요하면 같은 자리에서 새로 받아 온다.
 *
 * Heroicons is MIT licensed. Copyright (c) Tailwind Labs, Inc.
 * https://github.com/tailwindlabs/heroicons/blob/master/LICENSE
 */

interface IconProps {
  /** px. 기본 20은 본문 옆에 놓기 좋은 크기다 */
  size?: number;
  className?: string | undefined;
}

/**
 * 공통 껍데기.
 *
 * `aria-hidden`이 기본이다 — 아이콘만 있는 버튼은 **버튼 쪽에** `aria-label`을
 * 달아야 한다. 아이콘이 스스로 이름을 말하면 버튼 이름과 두 번 읽힌다.
 */
function Icon({ size = 20, className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...(className === undefined ? {} : { className })}
    >
      {children}
    </svg>
  );
}

/** heroicons: inbox — 아무것도 없을 때 */
export function InboxIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H6.911a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661Z" />
    </Icon>
  );
}

/** heroicons: arrow-left — 돌아가기 */
export function ArrowLeftIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
    </Icon>
  );
}

/** heroicons: cog-6-tooth — 설정 */
export function CogIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </Icon>
  );
}

/** heroicons: magnifying-glass — 사람 찾기 */
export function SearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </Icon>
  );
}

/** heroicons: squares-2x2 — 내 목록(격자) */
export function SquaresIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
    </Icon>
  );
}

/** heroicons: pencil — 사진 바꾸기 */
export function PencilIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
    </Icon>
  );
}

/** heroicons: plus — 추가 */
export function PlusIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4.5v15m7.5-7.5h-15" />
    </Icon>
  );
}

/** heroicons: users — 사람 */
export function UsersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </Icon>
  );
}

/** heroicons: clock — 내 타임라인 */
export function ClockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </Icon>
  );
}

/**
 * heroicons: lock-closed (solid) — 비공개 계정.
 *
 * 이것만 **solid**다. 선으로 그리면 이름 옆 작은 크기에서 자물쇠인지 안
 * 보인다. 상태를 말하는 표시라 한눈에 읽혀야 한다.
 */
export function LockIcon({ size = 20, className }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      {...(className === undefined ? {} : { className })}
    >
      <path
        fillRule="evenodd"
        d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/** heroicons: check-circle — 알림(잘 됐다) */
export function CheckCircleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </Icon>
  );
}

/** heroicons: exclamation-circle — 알림(잘 안 됐다) */
export function ExclamationCircleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
    </Icon>
  );
}

/** heroicons: check — 플랜 표의 "된다" */
export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m4.5 12.75 6 6 9-13.5" />
    </Icon>
  );
}

/** heroicons: minus — 플랜 표의 "아직 안 된다". ✕는 "틀렸다"로 읽혀 쓰지 않는다 */
export function MinusIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 12h14" />
    </Icon>
  );
}

/**
 * lucide: infinity — 상한이 없다는 뜻.
 *
 * 숫자 자리에 글자("무제한") 대신 기호를 둔다. 옆 칸이 `3/5`처럼 짧은
 * 숫자라, 네 글자가 들어가면 그 줄만 폭이 튀어 표가 흔들린다. 뜻은
 * 읽어 주는 기기에 말로 따로 준다.
 */
export function InfinityIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4Zm0 0c2 2.67 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.33-6 4Z" />
    </Icon>
  );
}
